import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import debounce from "lodash.debounce";
import NarrowInputField from "../../components/input/new/narrowInputField";
import RadioButton from "../../components/input/new/radioButton";
import ComboBoxAutoComplete from "../../components/input/comboBoxAutoComplete";
import { supabase } from "../../supabaseClient";
import "./clientDetails.css";
import ClientOnboardingHeader from "../../components/clientToolbar/clientOnboardingHeader";
import Footer from "../../components/footer";
import BackIcon from "../assets/backIcon.png";
import ClientLoginPopup from "../../home/new/components/clientLoginPopup";
import { useDispatch } from "react-redux";
import { fetchClient, updateClientData } from "../../redux/clientDataSlice";
import { authenticatedFetch } from "../../network/api";

const geocodeCache = {};

export default function ClientDetails() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const rawSearch = location.search;
  const queryParams = new URLSearchParams(rawSearch);
  const isAppointmentFlow = Boolean(queryParams.get("lawyerId"));
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [gender, setGender] = useState("");
  const [dob, setDob] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [addressDetails, setAddressDetails] = useState({
    city: "",
    state: "",
    zipCode: "",
    latitude: null,
    longitude: null,
  });

  // Address autocomplete states
  const [addressInput, setAddressInput] = useState("");
  const [autocompleteResults, setAutocompleteResults] = useState([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const openLoginPopup = () => setShowLoginPopup(true);
  const closeLoginPopup = () => setShowLoginPopup(false);

  // Fetch suggestions from HERE Autocomplete API
  const fetchAutocomplete = useCallback(
    debounce(async (query) => {
      if (query.length < 3) {
        setAutocompleteResults([]);
        return;
      }
      setIsLoading(true);
      setError("");
      try {
        const res = await fetch(
          `https://autocomplete.search.hereapi.com/v1/autocomplete?q=${encodeURIComponent(
            query
          )}&apiKey=${process.env.REACT_APP_HERE_API_KEY}`
        );
        if (!res.ok) throw new Error(res.statusText);
        const { items } = await res.json();
        setAutocompleteResults(items);
      } catch (e) {
        setError("Failed to fetch address suggestions.");
      } finally {
        setIsLoading(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    fetchAutocomplete(addressInput);
    return () => fetchAutocomplete.cancel();
  }, [addressInput, fetchAutocomplete]);

  const fetchGeocode = async (address) => {
    if (geocodeCache[address]) return geocodeCache[address];
    try {
      const res = await fetch(
        `https://geocode.search.hereapi.com/v1/geocode?q=${encodeURIComponent(
          address
        )}&apiKey=${process.env.REACT_APP_HERE_API_KEY}`
      );
      if (!res.ok) throw new Error(res.statusText);
      const { items } = await res.json();
      if (!items.length) return { latitude: null, longitude: null };
      const { lat, lng } = items[0].position;
      const geo = { latitude: lat, longitude: lng };
      geocodeCache[address] = geo;
      return geo;
    } catch {
      setError("Failed to retrieve geographic coordinates.");
      return { latitude: null, longitude: null };
    }
  };

  const handleAddressChange = (value) => {
    setAddressInput(value);
    setSelectedSuggestion(null);
    setAddressDetails({
      city: "",
      state: "",
      zipCode: "",
      latitude: null,
      longitude: null,
    });
  };

  const handleSelectSuggestion = async (suggestion) => {
    if (typeof suggestion === "string") {
      setAddressInput(suggestion);
      setSelectedSuggestion(null);
      setAddressDetails({
        city: "",
        state: "",
        zipCode: "",
        latitude: null,
        longitude: null,
      });
    } else {
      setSelectedSuggestion(suggestion);
      setAddressInput(suggestion.title);
      setAutocompleteResults([]);

      const city = suggestion.address?.city || "";
      const state = suggestion.address?.state || "";
      const zipCode = suggestion.address?.postalCode || "";

      setAddressDetails((d) => ({ ...d, city, state, zipCode }));

      const geo = await fetchGeocode(suggestion.title);
      setAddressDetails((d) => ({
        ...d,
        latitude: geo.latitude,
        longitude: geo.longitude,
      }));
    }
  };

  const handleContinue = async () => {
    // Collect your client details including selectedSuggestion.title or addressInput
    // ...

    const {
      data: { user },
      error: getUserError,
    } = await supabase.auth.getUser();
    if (getUserError || !user) {
      console.error("Error fetching user:", getUserError);
      alert("Unable to get user information.");
      return;
    }

    // Prepare payload
    const clientInfo = {
      first_name: firstName,
      last_name: lastName,
      city: addressDetails.city,
      zipCode: addressDetails.zipCode,
      dob,
      phone,
      state: addressDetails.state,
      gender,
      latitude: addressDetails.latitude,
      longitude: addressDetails.longitude,
      address: selectedSuggestion?.title || addressInput,
    };

    // Call your backend API
    try {
      const response = await fetch("/api/client/createClientProfile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: user.id,
          email: user.email,
          clientInfo,
        }),
      });
      if (!response.ok) {
        const err = await response.json();
        console.error("API error:", err);
        alert("Failed to save client details.");
        return;
      }

      const { success, clientData } = await response.json();
      if (!success) {
        // you might want to guard against unexpected API errors…
        alert("Server said: ERROR");
        return;
      }
      dispatch(updateClientData(clientData));

      if (isAppointmentFlow) {
        // preserve the entire querystring when you jump to intake form
        navigate(`/client/intake-form${rawSearch}`);
      } else {
        // regular flow
        navigate("/");
      }
    } catch (err) {
      console.error("Network error:", err);
      alert("Something went wrong.");
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div className="client-details-page-container">
        <ClientOnboardingHeader openLoginPopup={openLoginPopup} />
        <div>
          <div className="header-div">
            <img
              src={BackIcon}
              onClick={() => navigate(-1)}
              style={{ width: "24px", height: "24px" }}
              alt="back icon"
            />
            <h1 className="client-details-header-title">Create an account</h1>
          </div>

          <div className="client-details-name-row">
            <NarrowInputField
              name="First Name"
              title="Legal first name"
              placeholder="Enter your first name"
              isRequired
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              halfWidth
            />
            <NarrowInputField
              name="Last Name"
              title="Legal last name"
              placeholder="Enter your last name"
              isRequired
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              halfWidth
            />
          </div>

          <NarrowInputField
            name="Date of Birth"
            title="Date of Birth"
            placeholder="mm/dd/yyyy"
            isRequired
            value={dob}
            onChange={(e) => setDob(e.target.value)}
            halfWidth={false}
          />

          <NarrowInputField
            name="Phone"
            title="Phone Number"
            placeholder="Please enter your phone number without any special characters"
            isRequired
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            halfWidth={false}
          />

          <div className="radio-row">
            <p className="row-title"> Gender </p>
            <RadioButton
              label="Female"
              name="gender"
              value="female"
              checked={gender === "female"}
              onChange={(e) => setGender(e.target.value)}
            />
            <RadioButton
              label="Male"
              name="gender"
              value="male"
              checked={gender === "male"}
              onChange={(e) => setGender(e.target.value)}
            />
          </div>

          <div style={{ height: "1.5rem" }} />

          {/* Address Autocomplete Field */}

          <ComboBoxAutoComplete
            title="Address"
            placeholder="Start typing address..."
            options={autocompleteResults}
            value={selectedSuggestion}
            inputValue={addressInput}
            onInputChange={handleAddressChange}
            onChange={handleSelectSuggestion}
            getOptionLabel={(item) => item.title}
            isLoading={isLoading}
            error={error}
          />

          <button
            className="client-details-continue-button"
            onClick={handleContinue}
          >
            Continue
          </button>
        </div>
      </div>

      {showLoginPopup && (
        <ClientLoginPopup show={showLoginPopup} onClose={closeLoginPopup} />
      )}
      <Footer />
    </div>
  );
}
